import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
	faTrashAlt,
	faClose,
	faChevronDown,
	faLink,
} from '@fortawesome/free-solid-svg-icons'

import PopoverButton from '../PopoverButton/index.vue'
import { currencyFormat } from '@/helpers/currency-format'

library.add(faTrashAlt, faClose, faChevronDown, faLink)

@Component({
	components: {
		PopoverButton,
	},
})
export default class CTransacaoPrevidenciaria extends Vue {
	@Prop({ default: '#aaa', type: String }) color!: string
	@Prop({ required: true, type: String }) id!: string
	@Prop({ required: true, type: Number }) infoId!: number

	form = {
		parcel: 'Transação Individual',
		payment: 'A',
		recoverability: 'Alta perspectiva',
		discount: 65,
		debcads: 10,
		migration: {
			parcels: 0,
			value: '0',
		},
		consolidated: {
			principal: '0',
			fine: '0',
			interest: '0',
			legalFee: '0',
		},
		deductions: {
			legalPrejudice: '0',
			negativeCSLL: '0',
			limit: 70,
			usable: '0',
			used: '0',
			residual: '0',
		},
		paymentFlow: {
			downPayment: {
				percent: 4,
				value: '0',
				parcels: '0',
				parcelValue: '0',
			},
			remainingBalance: {
				value: '0',
				parcels: 120,
				parcelValue: '0',
			},
		},
	}

	advanced = {
		selic: 1.25,
	}

	money = {
		decimal: ',',
		thousands: '.',
		precision: 2,
		masked: false,
	}

	options = {
		tier: [
			'Irrecuperável',
			'Difícil recuperação',
			'Média perspectiva',
			'Alta perspectiva',
		],
		payment: ['A', 'B', 'C', 'D'],
		debit: ['Previdenciário', 'Não previdenciário'],
		parcels: [
			'Transação Individual',
			'Transação Por Adesão',
			'Parcelamento Ordinário',
			'Transação Individual Simplificada',
		],
	}

	get consolidatedTotal() {
		return currencyFormat(
			parseFloat(
				this.form.consolidated.principal
					.replaceAll('.', '')
					.replaceAll(',', '.'),
			) +
				parseFloat(
					this.form.consolidated.fine
						.replaceAll('.', '')
						.replaceAll(',', '.'),
				) +
				parseFloat(
					this.form.consolidated.interest
						.replaceAll('.', '')
						.replaceAll(',', '.'),
				) +
				parseFloat(
					this.form.consolidated.legalFee
						.replaceAll('.', '')
						.replaceAll(',', '.'),
				),
		)
	}

	get deal() {
		const consolidatedTotal = parseFloat(
			this.consolidatedTotal.replaceAll('.', '').replaceAll(',', '.'),
		)

		const total = consolidatedTotal * (1 - this.form.discount / 100)

		const diff = consolidatedTotal - total

		return {
			total: currencyFormat(this.parse(total)),
			discount: currencyFormat(this.parse(diff)),
		}
	}

	get deductions() {
		const legalPrejudice = parseFloat(
			this.form.deductions.legalPrejudice
				.replaceAll('.', '')
				.replaceAll(',', '.'),
		)

		const negativeCSLL = parseFloat(
			this.form.deductions.negativeCSLL
				.replaceAll('.', '')
				.replaceAll(',', '.'),
		)

		const total = legalPrejudice + negativeCSLL
		const dealTotal = parseFloat(
			this.deal.total.replaceAll('.', '').replaceAll(',', '.'),
		)
		const usable = dealTotal * (this.form.deductions.limit / 100)
		const used = usable <= total ? usable : total
		const residual = dealTotal - used

		return {
			total: currencyFormat(this.parse(total)),
			usable: currencyFormat(this.parse(usable)),
			used: currencyFormat(this.parse(used)),
			residual: currencyFormat(this.parse(residual)),
		}
	}

	get downPayment() {
		const value =
			parseFloat(
				this.deductions.residual
					.replaceAll('.', '')
					.replaceAll(',', '.'),
			) *
			(this.form.paymentFlow.downPayment.percent / 100)

		return currencyFormat(this.parse(value))
	}

	get downPaymentOptions() {
		const downPayment = parseFloat(
			this.downPayment.replaceAll('.', '').replaceAll(',', '.'),
		)

		const parcels = [] as Array<any>
		let parcelsNumber = 12
		for (let n = 1; n <= 12; n++) {
			let parcelValue = downPayment / n
			if (parcelValue >= 100) {
				parcels.push(
					n + 'x de R$' + currencyFormat(this.parse(parcelValue)),
				)
			}
		}

		return parcels
	}

	get remainingBalance() {
		const downPayment = parseFloat(
			this.downPayment.replaceAll('.', '').replaceAll(',', '.'),
		)

		const residual = parseFloat(
			this.deductions.residual.replaceAll('.', '').replaceAll(',', '.'),
		)

		const value = residual - downPayment
		let parcels = this.form.paymentFlow.remainingBalance.parcels
		// @ts-ignore
		if (parcels === '0' || parcels === '') {
			parcels = 1
		}

		const parcelValue = value / parcels

		const chartDataMonthly = [this.parse(parcelValue)]
		const chartDataRecuperate = [this.parse(value)]
		if (this.form.migration.value) {
			chartDataMonthly.push(
				parseFloat(
					this.form.migration.value
						.replaceAll('.', '')
						.replaceAll(',', '.'),
				),
			)
			if (this.form.migration.parcels) {
				chartDataRecuperate.push(
					parseFloat(
						this.migrationTotal
							.replaceAll('.', '')
							.replaceAll(',', '.'),
					),
				)
			}
		}
		this.$emit('savings', chartDataMonthly)
		this.$emit('recuperate', chartDataRecuperate)

		return {
			value: currencyFormat(this.parse(value)),
			parcelValue: currencyFormat(this.parse(parcelValue)),
		}
	}

	get legalPrejudiceDisabled() {
		if (
			this.form.parcel === 'Transação Individual' ||
			this.form.parcel === 'Transação Por Adesão'
		) {
			if (
				this.form.recoverability === 'Irrecuperável' ||
				this.form.recoverability === 'Difícil recuperação'
			) {
				return false
			}
		}

		this.form.deductions.legalPrejudice = '0'
		return true
	}

	get migrationTotal() {
		const migrationParcelValue = parseFloat(
			this.form.migration.value.replaceAll('.', '').replaceAll(',', '.'),
		)
		const total = this.form.migration.parcels * migrationParcelValue

		return currencyFormat(this.parse(total))
	}

	get calcsEach() {
		return this.$store.state.calculators.calcsEach
	}

	get calcsPrint() {
		return this.$store.state.calculators.calcsPrint
	}

	maxValue(max, parentObj, fieldName) {
		if (parseInt(parentObj[fieldName]) > max) {
			this.$nextTick(() => (parentObj[fieldName] = max))
		}
	}

	parse(n) {
		if (isNaN(n)) return 0
		n = parseFloat(n)
		return parseFloat(n.toFixed(3))
	}

	save() {
		this.savePrint()
		const found = this.calcsEach.find((item) => this.id === item.id)

		if (found) {
			found.selic = this.advanced.selic
			found.form = this.form
		} else {
			this.calcsEach.unshift({
				id: this.id,
				selic: this.advanced.selic,
				form: this.form,
			})
		}
	}

	savePrint() {
		const found = this.calcsPrint.find((item) => this.id === item.id)

		const rows = () => {
			const rows = [
				[
					{
						label: 'Tipo de Parcelamento',
						value: this.form.parcel,
					},
					{
						label: 'Capacidade de Pagamento',
						value: this.form.payment,
					},
					{
						label: 'Grau de Recuperabilidade',
						value: this.form.recoverability,
					},
				],
				[
					{
						label: 'Número de DEBCADs',
						value: this.form.debcads,
					},
					{
						label: 'Desconto',
						value: this.form.discount + ' %',
					},
				],
				[
					{
						label: 'Débito Consolidado',
						value: '',
						colSpan: 1,
						labelStyle: 'labelHeader',
					},
					{
						label: 'Débito Em Negociação',
						value: '',
						colSpan: 1,
						labelStyle: 'labelHeader',
					},
				],
				[
					{
						label: 'Total',
						value: 'R$ ' + this.consolidatedTotal,
					},
					{
						label: 'Desconto',
						value: 'R$ ' + this.deal.discount,
					},
					{
						label: 'Total',
						value: 'R$ ' + this.deal.total,
					},
				],
				[
					{
						label: 'Fluxo de Pagamento',
						value: '',
						colSpan: 3,
						labelStyle: 'labelHeader',
					},
				],
				[
					{
						label: 'Entrada',
						value:
							'R$ ' +
							this.downPayment +
							' em ' +
							this.form.paymentFlow.downPayment.value,
						colSpan: 2,
					},
					{},
					{
						label: 'Saldo Pós Entrada',
						value:
							'R$ ' +
							this.remainingBalance.value +
							' em ' +
							this.form.paymentFlow.remainingBalance.parcels +
							'x de R$ ' +
							this.remainingBalance.parcelValue,
						colSpan: 2,
					},
				],
			]

			if (this.form.migration.parcels > 0) {
				rows.push([
					{
						label: 'Migração de Parcelamento',
						value:
							'R$ ' +
							this.migrationTotal +
							' em ' +
							this.form.migration.parcels +
							'x de R$ ' +
							this.form.migration.value,
						colSpan: 3,
					},
				])
			}

			return rows
		}

		if (found) {
			found.rows = rows()
		} else {
			this.calcsPrint.unshift({
				id: this.id,
				title: 'Transação Tributária - Previdenciária',
				color: this.color,
				columns: 4,
				rows: rows(),
				type: 'grid',
			})
		}
	}

	created() {
		const found = this.calcsEach.find((item) => this.id === item.id)

		if (found) {
			this.advanced.selic = found.selic
			this.form = found.form
		}
	}
}
